// "use client" is required — this is a shared UI package consumed by Next.js apps,
// and consumers expect client components to be marked at the library level.
"use client";

import { clsx } from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { forwardRef, type ElementType } from "react";
import {
  containerStyles,
  iconStyles,
  innerStyles,
  loadingStyles,
} from "../Button/Button";
import { Icon, type IconProps } from "../Icon";
import { Loading } from "../Loading";

const MotionLoading = motion(Loading);

type LinkButtonProps<T extends ElementType = "a"> =
  React.ComponentPropsWithoutRef<T> & {
    as?: T;
    size?: "sm" | "md" | "lg";
    variant?: "primary" | "secondary" | "destructive";
    block?: boolean;
    leadingIcon?: IconProps["icon"];
    trailingIcon?: IconProps["icon"];
    // An anchor can't be natively disabled, so we mirror Button's disabled
    // styling and block interaction via pointer-events/tab order.
    disabled?: boolean;
    // Mirrors Button's `loading`: spinner + hidden label while an action the
    // link triggers is pending (e.g. a slow navigation); blocks re-interaction.
    loading?: boolean;
  };

const LinkButtonInner = <T extends ElementType = "a">(
  {
    as,
    size = "md",
    variant = "primary",
    leadingIcon,
    trailingIcon,
    block,
    disabled,
    loading,
    children,
    className,
    ...props
  }: LinkButtonProps<T> & { ref?: React.Ref<React.ElementRef<T>> },
  ref: React.Ref<React.ElementRef<T>>,
) => {
  const Component = as || "a";

  return (
    <Component
      ref={ref}
      className={clsx(
        containerStyles.base,
        containerStyles.size[size],
        containerStyles.variant[variant],
        block && containerStyles.block,
        disabled && containerStyles.disabled,
        // Loading blocks interaction but must NOT dim (containerStyles.disabled
        // adds opacity-50, which would also fade the spinner) — pointer guard only.
        loading && "pointer-events-none",
        className,
      )}
      aria-disabled={disabled || undefined}
      aria-busy={loading || undefined}
      // Take it out of the tab order while disabled OR loading, so keyboard
      // interaction is blocked to match the pointer-events styling.
      tabIndex={disabled || loading ? -1 : undefined}
      {...props}
    >
      <AnimatePresence mode="wait">
        {loading && (
          <MotionLoading
            initial={{ opacity: 0, position: "absolute", scale: 0.75 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.75 }}
            className={clsx(loadingStyles.variant[variant])}
            size="sm"
            variant={variant === "primary" ? "light" : "default"}
          />
        )}
      </AnimatePresence>
      <motion.div
        className={innerStyles.base}
        animate={{ opacity: loading ? 0 : 1, scale: loading ? 0.95 : 1 }}
      >
        {leadingIcon && (
          <Icon
            className={clsx(iconStyles.variant[variant], iconStyles.size[size])}
            icon={leadingIcon}
          />
        )}
        <div>{children}</div>
        {trailingIcon && (
          <Icon
            className={clsx(iconStyles.variant[variant], iconStyles.size[size])}
            icon={trailingIcon}
          />
        )}
      </motion.div>
    </Component>
  );
};

// Type assertion is needed because forwardRef doesn't support generic type
// parameters. This is the standard pattern for polymorphic forwardRef components.
export const LinkButton = forwardRef(LinkButtonInner) as <
  T extends ElementType = "a",
>(
  props: LinkButtonProps<T> & { ref?: React.Ref<React.ElementRef<T>> },
) => React.ReactElement | null;

(LinkButton as React.FC).displayName = "LinkButton";
